/* eslint-disable jsx-a11y/autocomplete-valid */
import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { IAlternativeCruise } from '../../interfaces/ICruise';
import { __ } from '../../helpers/TranslationService';
import { useActions } from '../../hooks/useActions';
import useCountryOptions from '../../hooks/useCountryOptions';
import { controlsEnum, FieldsEnumKeys } from '../../hooks/usePersonalData';
import { IRequestFormReducer } from '../../interfaces/IRequestForm';
import { ErrorsComponent, InputComponent } from '../RequestFormPersonalInfo/ControlComponents';
import RequestFormPersonalInfo_Row from '../RequestFormPersonalInfo/RequestFormPersonalInfo_Row';

import { normalizePhoneCode } from '../../hooks/usePersonalData';
import {
    LinkWithoutFlight,
    ExternalLinkIconStyled,
} from '../../components/CruiseInquiryForm/CruiseInquiryFlightSelection';
import { ContactViaEmailSection } from '../../pages/RequestForm/components/PersonalDataForm';
import { updateRequestFormFields } from '../../actions/requestFormActions';

interface IRequestFormPersonalInfo {
    controlsEnum: controlsEnum;
    requestForm: IRequestFormReducer;
    alternativeCruise?: IAlternativeCruise;
    updateRequestFormFields: typeof updateRequestFormFields;
    showEmailOnlyContactCheckbox?: boolean;
    isSubmitError?: boolean;
    apiValidationErrors?: { field: string; errorKey: string }[];
}

enum GenderTypes {
    male = 'male',
    female = 'female',
}

const compName = 'personalData';
const controlClass = 'form-control';

const SubTextStyles = css`
    padding: 3px 12px 0 0;
    margin-top: 10px;
    font-size: 12px;
`;

const SubTextLinkStyles = ({ theme }) => css`
    color: ${theme.link.primary};
    text-decoration: none;
`;

const PersonalDataLegalText = styled.div`
    ${SubTextStyles}
    a {
        ${SubTextLinkStyles}
    }
`;

const NewsletterSubscription = styled.span`
    ${SubTextStyles}
    font-weight: 400;
    a {
        ${SubTextLinkStyles}
    }
`;

const StyledContactViaEmail = styled(ContactViaEmailSection)`
    margin: 0;
`;

const RequestFormPersonalInfo = (props: IRequestFormPersonalInfo) => {
    const { controlsEnum, requestForm, alternativeCruise } = props;

    const [genderSelectedVal, setGenderValue] = React.useState('');

    const countryOptions = useCountryOptions(
        controlsEnum[FieldsEnumKeys.countryCode].control.value as string,
    );

    const { setAirportCode } = useActions();
    const handleSetAirportCode = (e) => {
        let value: string = e?.target?.value;
        if (value) {
            setAirportCode(value);
        }
    };

    const handleGenderRadioChange = (type: GenderTypes) => {
        controlsEnum[FieldsEnumKeys.isFemale].control.onBlur({
            target: {
                type: 'radio',
                checked: type === GenderTypes.female,
            },
        });
    };

    const handlePhoneCode = (e: React.ChangeEvent<HTMLInputElement>) => {
        controlsEnum[FieldsEnumKeys.phoneCountryCode].control.onBlur({
            target: {
                value: e.target.value,
            },
        });
    };

    const handleContactViaEmailChange = (event) =>
        props.updateRequestFormFields({
            isContactViaEmailOnly: event.target.checked,
        });

    const genderControlValue = controlsEnum[FieldsEnumKeys.isFemale].control.value;

    React.useEffect(() => {
        controlsEnum[FieldsEnumKeys.phoneCountryCode].control.onBlur({
            target: {
                value: normalizePhoneCode(countryOptions.selectedLabel),
            },
        });
    }, [countryOptions.selectedLabel]);

    React.useEffect(() => {
        if (genderControlValue === true) {
            setGenderValue(GenderTypes.female);
        }
        if (genderControlValue === false) {
            setGenderValue(GenderTypes.male);
        }
    }, [genderControlValue]);

    const isPhoneCodeInvalid =
        controlsEnum[FieldsEnumKeys.phoneCountryCode].control.isDirty &&
        !controlsEnum[FieldsEnumKeys.phoneCountryCode].control.isValid;
    const isPhoneInvalid =
        controlsEnum[FieldsEnumKeys.phone].control.isDirty &&
        !controlsEnum[FieldsEnumKeys.phone].control.isValid;

    const hasValidationErrors = props.apiValidationErrors && props.apiValidationErrors.length;

    return (
        <section className="personalDataContainer">
            <form className="personalData__form">
                <RequestFormPersonalInfo_Row
                    label={__('personalData.salutation', 'dreamlines')}
                    field={controlsEnum[FieldsEnumKeys.isFemale]}
                >
                    <>
                        <div className={`personalData__form__genderInputContainer`}>
                            <label>
                                {__('personalData.female', 'dreamlines')}
                                <input
                                    type="radio"
                                    name="genderCheckbox"
                                    checked={genderSelectedVal === GenderTypes.female}
                                    onChange={() => handleGenderRadioChange(GenderTypes.female)}
                                />
                            </label>
                            <label>
                                {__('personalData.male', 'dreamlines')}
                                <input
                                    type="radio"
                                    name="genderCheckbox"
                                    checked={genderSelectedVal === GenderTypes.male}
                                    onChange={() => handleGenderRadioChange(GenderTypes.male)}
                                />
                            </label>
                        </div>
                        {controlsEnum[FieldsEnumKeys.isFemale].control.isDirty &&
                            !controlsEnum[FieldsEnumKeys.isFemale].control.isValid && (
                                <ErrorsComponent errors={[__('general.required', 'dreamlines')]} />
                            )}
                    </>
                </RequestFormPersonalInfo_Row>
                <RequestFormPersonalInfo_Row
                    label={`${__('personalData.firstname', 'dreamlines')} *`}
                    type="text"
                    field={controlsEnum[FieldsEnumKeys.firstname]}
                />
                <RequestFormPersonalInfo_Row
                    label={__('personalData.lastname', 'dreamlines')}
                    type="text"
                    field={controlsEnum[FieldsEnumKeys.lastname]}
                />
                <RequestFormPersonalInfo_Row
                    label={__('personalData.countryCode', 'dreamlines')}
                    field={controlsEnum[FieldsEnumKeys.countryCode]}
                />
                <RequestFormPersonalInfo_Row
                    label={__('personalData.phone', 'dreamlines')}
                    field={controlsEnum[FieldsEnumKeys.phone]}
                >
                    <>
                        <div className={`${compName}__form__input__ccp`}>
                            <div className={`${compName}__form__input__ccp__country-code`}>
                                <span className={`${compName}__form__input__ccp__prefix`}>+</span>
                                <input
                                    id="code"
                                    className={`${controlClass}__input${
                                        isPhoneCodeInvalid ? '--invalid' : ''
                                    }`}
                                    type="text"
                                    name="code"
                                    key={countryOptions.selectedLabel}
                                    title={__('general.required', 'dreamlines')}
                                    defaultValue={normalizePhoneCode(countryOptions.selectedLabel)}
                                    onBlur={handlePhoneCode}
                                />
                            </div>
                            <input
                                id={FieldsEnumKeys.phone}
                                defaultValue={requestForm[FieldsEnumKeys.phone]}
                                name={FieldsEnumKeys.phone}
                                title={__('general.required', 'dreamlines')}
                                className={`${controlClass}__input${
                                    isPhoneInvalid ? '--invalid' : ''
                                } ${compName}__form__input__ccp_phone`}
                                type={'tel'}
                                onBlur={controlsEnum[FieldsEnumKeys.phone].control.onBlur}
                                onChange={controlsEnum[FieldsEnumKeys.phone].control.onChange}
                            />
                        </div>
                        {(isPhoneInvalid || isPhoneCodeInvalid) && (
                            <ErrorsComponent
                                errors={
                                    controlsEnum[
                                        FieldsEnumKeys[
                                            `${isPhoneInvalid ? 'phone' : 'phoneCountryCode'}`
                                        ]
                                    ].control.errors
                                }
                            />
                        )}
                    </>
                </RequestFormPersonalInfo_Row>
                <RequestFormPersonalInfo_Row
                    title={__('general.required', 'dreamlines')}
                    label={__('personalData.email', 'dreamlines')}
                    type="email"
                    field={controlsEnum[FieldsEnumKeys.email]}
                />

                {props.showEmailOnlyContactCheckbox && (
                    <div className="form-row">
                        <div className="form-row__cell"></div>
                        <div className="form-row__cell">
                            <StyledContactViaEmail htmlFor="isContactViaEmailOnly">
                                <input
                                    id="isContactViaEmailOnly"
                                    name="isContactViaEmailOnly"
                                    type="checkbox"
                                    checked={requestForm.isContactViaEmailOnly}
                                    onChange={handleContactViaEmailChange}
                                />
                                <span>{__('contactViaEmailOnly', 'dreamlines')}</span>
                            </StyledContactViaEmail>
                        </div>
                    </div>
                )}

                <RequestFormPersonalInfo_Row
                    label={__('personalData.message', 'dreamlines')}
                    field={controlsEnum[FieldsEnumKeys.message]}
                    className="personalData__form__messageArea"
                />

                <RequestFormPersonalInfo_Row
                    label={__('cruiseData.flightMandatory', 'dreamlines')}
                    title={__('general.required', 'dreamlines')}
                    field={controlsEnum[FieldsEnumKeys.selectedAirportIndex]}
                    onChange={handleSetAirportCode}
                />
                {alternativeCruise?.includesFlight === false && (
                    <div className="form-row">
                        <div className="form-row__cell"></div>
                        <div className="form-row__cell">
                            <LinkWithoutFlight>
                                <LinkWithoutFlight href={alternativeCruise?.url}>
                                    {__('To Cruise without Flight', 'dreamlines')}
                                    <ExternalLinkIconStyled />
                                </LinkWithoutFlight>
                            </LinkWithoutFlight>
                        </div>
                    </div>
                )}

                <div>
                    {!!props.isSubmitError && !!hasValidationErrors && (
                        <p className="submitError">
                            {__(props.apiValidationErrors[0].errorKey, 'dreamlines')}
                        </p>
                    )}

                    {props.isSubmitError && !hasValidationErrors && (
                        <p className="submitError">{__('Something went wrong', 'dreamlines')}</p>
                    )}
                </div>
                <div className={`${compName}__form__cb-newsletter`}>
                    <div className={`${compName}__form__cb-newsletter__cb-holder`}>
                        <InputComponent
                            type="checkbox"
                            className={`${compName}__form__cb-newsletter__cb-holder__cb`}
                            field={controlsEnum[FieldsEnumKeys.newsletterSubscription]}
                        />
                    </div>
                    <label
                        htmlFor={FieldsEnumKeys.newsletterSubscription}
                        className={`${controlClass}__cb-label ${compName}__form__cb-newsletter__lbl`}
                    >
                        <NewsletterSubscription
                            dangerouslySetInnerHTML={{
                                __html: __('cruiseData.newsletterSub', 'dreamlines'),
                            }}
                        />
                    </label>
                </div>
                <PersonalDataLegalText
                    dangerouslySetInnerHTML={{
                        __html: __('requestPage.legalText', 'dreamlines'),
                    }}
                />
            </form>
        </section>
    );
};

export default RequestFormPersonalInfo;
